'use client';

import { ArrowUpRight, RefreshCw } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useSyncExternalStore } from 'react';
import { toast } from 'sonner';

import { useRequestStableBuild } from '@/hooks/mutations/dev/use-request-stable-build';
import { useStableBuildStatus } from '@/hooks/queries/dev/use-stable-build-status';
import type { StableBuildStatus } from '@/lib/schemas/dev/stable-build.schema';
import { cn } from '@/lib/utils/cn';

// Boutons de l'équipe sur dev.<domaine> (HMR : le travail en cours des sessions), à gauche du
// bouton des outils TanStack Query (en bas à droite, 48 px à 12 px du bord) : « Voir en stable »
// ouvre la même page sur stable.<domaine> (build de production du code committé,
// docs/infrastructure.md §4 point 9) ; « Recompiler stable » (super-admins) y porte la dernière
// version committée. Rendus en HMR sur dev seulement : ni sur stable, ni en prod.

export function stableHostFor(hostname: string, development: boolean): string | null {
  return development && hostname.startsWith('dev.') ? `stable.${hostname}` : null;
}

const noSubscription = () => () => {};

const PILL =
  'rounded-pill border-border-subtle text-fg-2 hover:text-fg-1 inline-flex h-8 items-center gap-1.5 border bg-white px-3 text-[12px] font-medium shadow-sm transition-colors hover:bg-neutral-50 focus-visible:ring-4 focus-visible:ring-purple-500/30 focus-visible:outline-none disabled:cursor-default disabled:hover:bg-white';

const short = (sha: string | null) => (sha ? sha.slice(0, 8) : '?');

export function DevEnvSwitch() {
  const t = useTranslations('common.envSwitch');
  // Hôte lu au client seulement : le rendu serveur ne l'affiche jamais (pas d'écart à l'hydratation).
  const hostname = useSyncExternalStore(
    noSubscription,
    () => window.location.hostname,
    () => null,
  );
  const stableHost = hostname
    ? stableHostFor(hostname, process.env.NODE_ENV === 'development')
    : null;
  if (!stableHost) return null;

  const open = () => {
    const { pathname, search, hash } = window.location;
    window.location.assign(`https://${stableHost}${pathname}${search}${hash}`);
  };

  return (
    <div className="fixed right-[calc(68px+env(safe-area-inset-right))] bottom-[calc(20px+env(safe-area-inset-bottom))] z-50 flex items-center gap-2">
      <RebuildStable />
      <button
        type="button"
        onClick={open}
        title={t('toStableHint', { host: stableHost })}
        className={PILL}
      >
        {t('toStable')}
        <ArrowUpRight aria-hidden className="size-3.5" strokeWidth={2.25} />
      </button>
    </div>
  );
}

export function RebuildStable() {
  const t = useTranslations('common.envSwitch');
  const { data: status } = useStableBuildStatus(true);
  const request = useRequestStableBuild();
  useBuildFinishedToast(status ?? null);
  // Route fermée à cet utilisateur (session, super-admin) ou outillage absent : pas de bouton.
  if (!status) return null;

  const busy = request.isPending || status.state === 'building' || status.state === 'requested';
  const failed = status.state === 'failed' && !busy;
  const label =
    request.isPending || status.state === 'requested'
      ? t('rebuildRequested')
      : status.state === 'building'
        ? t('rebuildBuilding')
        : failed
          ? t('rebuildFailed')
          : t('rebuild');
  const hint =
    status.state === 'building'
      ? t('rebuildBuildingHint', {
          commit: short(status.integration),
          served: short(status.served),
        })
      : failed
        ? t('rebuildFailedHint', {
            commit: short(status.integration),
            served: short(status.served),
          })
        : t('rebuildHint', { served: short(status.served) });

  return (
    <button
      type="button"
      disabled={busy}
      aria-busy={busy}
      onClick={() => request.mutate(undefined, { onError: () => toast.error(t('rebuildError')) })}
      title={hint}
      className={cn(PILL, failed && 'text-danger-700 hover:text-danger-700')}
    >
      <RefreshCw
        aria-hidden
        className={cn('size-3.5', busy && 'animate-spin motion-reduce:animate-none')}
        strokeWidth={2.25}
      />
      {label}
    </button>
  );
}

/** Une recompilation suivie qui aboutit se dit d'un toast. */
function useBuildFinishedToast(status: StableBuildStatus | null) {
  const t = useTranslations('common.envSwitch');
  const previous = useRef<StableBuildStatus['state'] | null>(null);
  useEffect(() => {
    const before = previous.current;
    previous.current = status?.state ?? null;
    if (!status || (before !== 'building' && before !== 'requested')) return;
    if (status.state === 'ready') toast.success(t('rebuildDone', { commit: short(status.served) }));
    if (status.state === 'failed')
      toast.error(
        t('rebuildFailedHint', { commit: short(status.integration), served: short(status.served) }),
      );
  }, [status, t]);
}
